import { del, get, patch, post } from "@/api/client";
import type { Role, SessionUser } from "@/api/auth";

export interface ProfileInput {
  job_title?: string;
  department?: string;
  team?: string;
  location?: string;
  employment_type?: string;
  seniority?: string;
  start_date?: string;
  language?: string;
}

export interface UserDetail extends SessionUser {
  profile: (ProfileInput & { full_name?: string }) | null;
}

export interface UpdateUserInput {
  full_name?: string | null;
  role?: "org_admin" | "manager" | "employee";
  password?: string;
  profile?: ProfileInput | null;
}

export interface CreateUserInput {
  email: string;
  role: Exclude<Role, "platform_admin">;
  full_name?: string | null;
  profile?: ProfileInput | null;
  // When true (default), the user is e-mailed an invitation link instead of a
  // password being set here. When false, `password` is required.
  invite?: boolean;
  password?: string;
}

export async function listUsers(signal?: AbortSignal): Promise<SessionUser[]> {
  const r = await get<SessionUser[]>("/api/users", signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function createUser(input: CreateUserInput): Promise<SessionUser> {
  const r = await post<SessionUser>("/api/users", input);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function getUserDetail(id: string, signal?: AbortSignal): Promise<UserDetail> {
  const r = await get<UserDetail>(`/api/users/${id}`, signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function updateUser(id: string, input: UpdateUserInput): Promise<SessionUser> {
  const r = await patch<SessionUser>(`/api/users/${id}`, input);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function deleteUser(id: string): Promise<void> {
  const r = await del(`/api/users/${id}`);
  if (!r.ok) throw new Error(r.err.message);
}

export interface ImportResult {
  created: number;
  failed: { email: string; error: string }[];
}

export async function importUsers(csvText: string): Promise<ImportResult> {
  const r = await post<ImportResult>("/api/users/import", { csv: csvText });
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}
